/**
 * Y-axis tick for an Azure resource-type bar chart (Spec 286 T159 item 21).
 *
 * A type such as `alertsmanagement/smartDetectorAlertRules` is wider than the axis, and recharts clips a single-line
 * tick at the SVG edge. This tick puts the provider on one line and the rest of the type on a second, each held to
 * a character budget, with the full type in an SVG <title> so it shows on hover.
 */
import React from 'react';

export interface ResourceTypeAxisLines {
  /** One line for a label with no provider (the "Other" bucket), else [provider, rest]. */
  lines: [string] | [string, string];
  full: string;
}

const fit = (s: string, maxChars: number): string => (s.length <= maxChars ? s : `${s.slice(0, maxChars - 1)}…`);

/** Split a resource type (provider prefix already trimmed) into the axis lines. */
export function resourceTypeAxisLines(label: string, maxChars: number): ResourceTypeAxisLines {
  const slash = label.indexOf('/');
  if (slash <= 0) return { lines: [fit(label, maxChars)], full: label };
  return { lines: [fit(label.slice(0, slash), maxChars), fit(label.slice(slash + 1), maxChars)], full: label };
}

interface ResourceTypeTickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value?: unknown };
  /** Characters per line; the axis is 190 px wide at 10 px text. */
  maxChars?: number;
}

export function ResourceTypeTick({ x = 0, y = 0, payload, maxChars = 30 }: ResourceTypeTickProps) {
  const { lines, full } = resourceTypeAxisLines(String(payload?.value ?? ''), maxChars);
  const twoLines = lines.length === 2;
  return (
    <text x={x} y={y} textAnchor="end" fontSize={10}>
      <title>{full}</title>
      <tspan
        x={x}
        dy={twoLines ? '-0.2em' : '0.35em'}
        style={{ fill: twoLines ? 'var(--color-text-secondary)' : 'var(--color-text-primary)' }}
      >
        {lines[0]}
      </tspan>
      {twoLines && (
        <tspan x={x} dy="1.15em" style={{ fill: 'var(--color-text-primary)' }}>
          {lines[1]}
        </tspan>
      )}
    </text>
  );
}
